import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Loader2, Trash2 } from "lucide-react";
import { LEVEL_STYLES } from "@/components/hfacs/hfacsLevels";

const STATUSES = ["À examiner", "Retenu", "Rejeté"];

export default function FactorDialog({ open, onOpenChange, factor, selection, facts, onSubmit, submitting, onDelete, deleting, canDelete }) {
  const [justification, setJustification] = useState("");
  const [commentaire, setCommentaire] = useState("");
  const [statut, setStatut] = useState("À examiner");
  const [factIds, setFactIds] = useState([]);
  const [confirmDelete, setConfirmDelete] = useState(false);

  useEffect(() => {
    if (open) {
      setJustification(selection?.JUSTIFICATION || "");
      setCommentaire(selection?.COMMENTAIRE || "");
      setStatut(selection?.STATUT_ANALYSE || "À examiner");
      setFactIds(selection?.FAITS_ASSOCIES ? selection.FAITS_ASSOCIES.split(",").map((s) => s.trim()).filter(Boolean) : []);
      setConfirmDelete(false);
    }
  }, [open, selection]);

  if (!factor) return null;
  const style = LEVEL_STYLES[factor.NIVEAU] || LEVEL_STYLES["Actes dangereux"];

  const toggleFact = (id) => {
    setFactIds((prev) => (prev.includes(id) ? prev.filter((f) => f !== id) : [...prev, id]));
  };

  const submit = (e) => {
    e.preventDefault();
    onSubmit({ id_facteur: factor.ID_FACTEUR, justification, commentaire, statut_analyse: statut, fact_ids: factIds });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="factor-dialog" className="max-w-lg">
        <DialogHeader>
          <span className={`inline-flex self-start font-mono text-[10px] uppercase px-2 py-0.5 rounded-sm border mb-2 ${style.badge}`}>{factor.NIVEAU}</span>
          <DialogTitle className="font-heading uppercase text-base leading-snug">{factor.LIBELLE}</DialogTitle>
          <DialogDescription>{factor.FAMILLE}</DialogDescription>
        </DialogHeader>

        {factor.DESCRIPTION && <p className="text-sm text-muted-foreground mb-2">{factor.DESCRIPTION}</p>}
        {factor.QUESTIONS_GUIDES && (
          <div className="mb-3 px-3 py-2 rounded-sm border border-accent/30 bg-accent/10 text-xs text-accent">
            {factor.QUESTIONS_GUIDES}
          </div>
        )}
        {/* fact_ids seeded from selection.FAITS_ASSOCIES below */}

        <form onSubmit={submit} className="space-y-3">
          <div>
            <Label>Statut</Label>
            <Select value={statut} onValueChange={setStatut}>
              <SelectTrigger data-testid="factor-form-statut-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                {STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>

          {facts?.length > 0 && (
            <div>
              <Label>Faits associés</Label>
              <div className="max-h-32 overflow-y-auto space-y-1.5 mt-1 border border-border rounded-sm p-2">
                {facts.map((f) => (
                  <label key={f.ID_FAIT} className="flex items-start gap-2 text-xs cursor-pointer">
                    <Checkbox data-testid={`factor-fact-checkbox-${f.ID_FAIT}`} checked={factIds.includes(f.ID_FAIT)} onCheckedChange={() => toggleFact(f.ID_FAIT)} />
                    <span className="leading-snug">{f.DESCRIPTION_FACTUELLE}</span>
                  </label>
                ))}
              </div>
            </div>
          )}

          <div>
            <Label>Justification</Label>
            <Textarea rows={3} data-testid="factor-form-justification" value={justification} onChange={(e) => setJustification(e.target.value)} />
          </div>
          <div>
            <Label>Commentaire</Label>
            <Textarea rows={2} data-testid="factor-form-commentaire" value={commentaire} onChange={(e) => setCommentaire(e.target.value)} />
          </div>
          {statut === "Retenu" && !justification.trim() && factIds.length === 0 && (
            <p className="text-xs text-rose-600">Un facteur retenu doit être relié à au moins un fait ou une justification.</p>
          )}
          <Button type="submit" disabled={submitting} data-testid="factor-form-submit" className="w-full bg-primary hover:bg-primary/90 text-primary-foreground">
            {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : "Enregistrer"}
          </Button>

          {canDelete && selection && (
            confirmDelete ? (
              <div className="flex items-center gap-2 pt-1">
                <p className="text-xs text-rose-600 flex-1">Confirmer la suppression définitive de ce facteur de l'analyse ?</p>
                <Button type="button" variant="destructive" size="sm" disabled={deleting} data-testid="factor-form-confirm-delete" onClick={() => onDelete(selection.ID)}>
                  {deleting ? <Loader2 className="h-4 w-4 animate-spin" /> : "Confirmer"}
                </Button>
                <Button type="button" variant="outline" size="sm" onClick={() => setConfirmDelete(false)}>Annuler</Button>
              </div>
            ) : (
              <Button type="button" variant="ghost" size="sm" data-testid="factor-form-delete" onClick={() => setConfirmDelete(true)} className="w-full text-rose-600 hover:text-rose-700 hover:bg-rose-500/10 gap-2">
                <Trash2 className="h-3.5 w-3.5" /> Supprimer ce facteur de l'analyse
              </Button>
            )
          )}
        </form>
      </DialogContent>
    </Dialog>
  );
}
